import React, { useState } from 'react'
import { Alert, Box, Button, Grid, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { ADMIN_ACCESS_MODES } from '../../utils/sessionUtils'
import useMembershipFunnel from './useMembershipFunnel'
import { formatSourceLabel, getAvailability, validateCustomRange } from './membershipFunnelUtils'
import { FunnelSummary, OrderSegments, panelSx, ResourceState, SectionTitle } from './MembershipFunnelPanels'

const RANGE_LABELS = { '7d': '최근 7일', '30d': '최근 30일', all: '전체 기간', custom: '직접 지정' }
const PLATFORM_LABELS = { ALL: '전체 OS', IOS: 'iOS', ANDROID: 'Android' }

function AvailabilityNote({ label, availability }) {
    return <Typography variant="caption" color="text.secondary" component="p">
        {label}: {availability?.status === 'ready' ? `${availability.availableFrom} ~ ${availability.dataThrough}` : '집계 준비 중'}
    </Typography>
}

function MembershipFunnelDashboard() {
    const { meta, metaError, metaLoading, notice, query, setQuery, summary, orders, refresh } = useMembershipFunnel()
    const [rangeSelection, setRangeSelection] = useState('7d')
    const [dates, setDates] = useState({ from: '', to: '' })
    const [dateError, setDateError] = useState('')
    const changeQuery = field => event => {
        const value = event.target.value
        setQuery(current => ({ ...current, [field]: value }))
    }
    const changeDate = field => event => {
        const value = event.target.value
        setDates(current => ({ ...current, [field]: value }))
    }

    const changeRange = event => {
        const range = event.target.value
        setRangeSelection(range)
        setDateError('')
        if (range !== 'custom') {
            setQuery(current => ({ ...current, range, from: undefined, to: undefined }))
        } else {
            setDates({ from: query.from || summary.data?.filter.from || '', to: query.to || summary.data?.filter.to || '' })
        }
    }
    const applyDates = event => {
        event.preventDefault()
        const error = validateCustomRange(dates.from, dates.to, meta.maxCustomRangeDays)
        setDateError(error)
        if (!error) setQuery(current => ({ ...current, range: 'custom', ...dates }))
    }
    const pendingDates = rangeSelection === 'custom' && (query.range !== 'custom' || dates.from !== query.from || dates.to !== query.to)

    return <Grid item xs={12} sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
            <Box>
                <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>멤버십 퍼널</Typography>
                <Typography variant="body2" color="text.secondary">멤버십 가입 유도 성과 · 진입부터 가입 완료까지의 흐름을 확인합니다.</Typography>
            </Box>
            <Button startIcon={<RefreshIcon />} onClick={refresh} disabled={metaLoading} sx={{ flexShrink: 0 }}>새로고침</Button>
        </Stack>
        {metaError && <Alert severity="error" sx={{ mb: 2 }}>{metaError}</Alert>}
        {notice && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}
        {!meta && metaLoading && <LinearProgress aria-label="멤버십 필터 불러오는 중" />}
        {meta && <>
            <Paper component="form" onSubmit={applyDates} sx={{
                ...panelSx,
                mb: 3,
                position: 'sticky',
                top: { xs: 56, sm: 64 },
                maxHeight: { xs: 'calc(100vh - 88px)', sm: 'calc(100vh - 96px)' },
                overflowY: 'auto',
                '@media (max-width: 599.95px) and (orientation: landscape)': {
                    top: 48,
                    maxHeight: 'calc(100vh - 96px)',
                },
                zIndex: theme => theme.zIndex.appBar - 1,
            }} aria-label="멤버십 퍼널 필터">
                <Stack direction="row" gap={1.5} flexWrap="wrap">
                    <TextField id="membership-range" select SelectProps={{ native: true }} size="small" label="기간 선택" value={rangeSelection} onChange={changeRange} sx={{ minWidth: 155 }}>
                        {meta.rangeOptions.map(range => <option key={range} value={range}>{RANGE_LABELS[range] || range}</option>)}
                    </TextField>
                    <TextField id="membership-platform" select SelectProps={{ native: true }} size="small" label="OS 선택" value={query.platform} onChange={changeQuery('platform')} sx={{ minWidth: 150 }}>
                        {meta.platformOptions.map(platform => <option key={platform} value={platform}>{PLATFORM_LABELS[platform] || platform}</option>)}
                    </TextField>
                    <TextField id="membership-source" select SelectProps={{ native: true }} size="small" label="진입점 선택" value={query.entrySource} onChange={changeQuery('entrySource')} sx={{ flex: '1 1 240px', minWidth: 0 }}>
                        <option value={meta.allOption.value}>{meta.allOption.label}{getAvailability(meta, query.platform, meta.allOption.value)?.status !== 'ready' ? ' (집계 준비 중)' : ''}</option>
                        {meta.entrySourceOptions.map(source => <option key={source.entrySource} value={source.entrySource}>
                            {formatSourceLabel(source)}{getAvailability(meta, query.platform, source.entrySource)?.status !== 'ready' ? ' (집계 준비 중)' : ''}
                        </option>)}
                    </TextField>
                </Stack>
                {rangeSelection === 'custom' && <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 2 }}>
                    <TextField id="membership-from" label="시작일" type="date" size="small" value={dates.from} onChange={changeDate('from')} InputLabelProps={{ shrink: true }} />
                    <TextField id="membership-to" label="종료일" type="date" size="small" value={dates.to} onChange={changeDate('to')} InputLabelProps={{ shrink: true }} />
                    <Button type="submit" variant="contained">기간 적용</Button>
                </Stack>}
                {dateError && <Alert severity="error" sx={{ mt: 1.5 }}>{dateError}</Alert>}
                {pendingDates && <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>날짜를 지정하고 기간 적용을 누르면 조회됩니다. 아래에는 이전 적용 기간의 결과가 표시됩니다.</Typography>}
                <Box sx={{ mt: 1.5 }}>
                    <AvailabilityNote label="퍼널 제공 범위" availability={getAvailability(meta, query.platform, query.entrySource)} />
                    <AvailabilityNote label="주문자 제공 범위" availability={getAvailability(meta, query.platform)} />
                    <Typography variant="caption" color="text.secondary">최근 기간은 각 통계의 마지막 수집 완료일을 기준으로 합니다. 같은 날짜로 비교하려면 두 제공 범위에 포함되는 기간을 직접 지정해 주세요.</Typography>
                </Box>
            </Paper>
            <Box component="section" aria-label="멤버십 퍼널 성과">
                <ResourceState resource={summary} label="퍼널 데이터" />
                {summary.data && <FunnelSummary key={`${query.platform}:${query.entrySource}`} data={summary.data} />}
            </Box>
            <Box component="section" aria-label="주문자 멤버십 통계" sx={{ mt: 4 }}>
                <SectionTitle note="전체 앱 주문 기준 · 진입점 필터 미적용">주문자 멤버십 구성</SectionTitle>
                <ResourceState resource={orders} label="주문자 데이터" />
                {orders.data && <OrderSegments data={orders.data} />}
            </Box>
        </>}
    </Grid>
}

export default function MembershipFunnel({ role, accessMode }) {
    if (!(role >= 2) || accessMode === ADMIN_ACCESS_MODES.COUNTRY_ANALYTICS_ONLY) {
        return <Grid item xs={12}><Alert severity="error">멤버십 퍼널 조회 권한이 없습니다.</Alert></Grid>
    }
    return <MembershipFunnelDashboard />
}
